import { PERMISSIONS } from "./useSession";
import type { SellerSession } from "@/lib/api/types";

// Positive grants only: a new invoice verb must never silently open administration.
const ADMINISTRATION_PERMISSIONS: ReadonlySet<string> = new Set([
  PERMISSIONS.MENU_MANAGE,
  PERMISSIONS.FLOOR_MANAGE,
  PERMISSIONS.COSTING_MANAGE,
  PERMISSIONS.REPORT_VIEW,
  PERMISSIONS.REPORT_OPERATIONS_VIEW,
  PERMISSIONS.SETTINGS_MANAGE,
  PERMISSIONS.TIER_MANAGE,
  PERMISSIONS.MEMBER_VIEW,
  PERMISSIONS.MEMBER_INVITE,
  PERMISSIONS.MEMBER_REMOVE,
  PERMISSIONS.MEMBER_EDIT_ROLES,
  PERMISSIONS.ROLE_VIEW,
  PERMISSIONS.ROLE_CREATE,
  PERMISSIONS.ROLE_EDIT,
  PERMISSIONS.ROLE_DELETE,
  PERMISSIONS.PAYMENT_VIEW,
  PERMISSIONS.PAYMENT_EDIT,
  PERMISSIONS.CATEGORY_CREATE,
  PERMISSIONS.CATEGORY_EDIT,
  PERMISSIONS.CATEGORY_DELETE,
  PERMISSIONS.PRODUCT_VIEW,
  PERMISSIONS.PRODUCT_EDIT,
  PERMISSIONS.STORE_EDIT,
  PERMISSIONS.DRIVER_DOCUMENTS_VIEW,
  PERMISSIONS.DRIVER_INVITE,
  PERMISSIONS.CUSTOMER_BLOCK,
  "DESIGN_VIEW",
  "DESIGN_EDIT",
  "DESIGN_PUBLISH",
]);

export function holdsAdministrationPermission(
  permissions: readonly string[] | undefined | null,
): boolean {
  return (permissions ?? []).some((permission) =>
    ADMINISTRATION_PERMISSIONS.has(permission),
  );
}

/** Shared page implementations, restricted to the cashier's existing work. */
export function canAccessManagementPath(
  permissions: readonly string[] | undefined | null,
  path: string,
): boolean {
  const held = permissions ?? [];
  if (holdsAdministrationPermission(held)) return true;
  if (path === "manage") return holdsBackOfficePermission(held);
  if (/^manage\/invoices(?:\/[^/]+)?$/.test(path)) {
    return (
      held.includes(PERMISSIONS.POS_USE) &&
      held.includes(PERMISSIONS.INVOICE_CLOSE)
    );
  }
  if (path === "manage/shift") return held.includes(PERMISSIONS.SHIFT_MANAGE);
  if (path === "manage/deliveries/new") return held.includes(PERMISSIONS.DELIVERY_RECORD);
  if (path === "manage/customers") return held.includes(PERMISSIONS.CUSTOMER_VIEW);
  if (/^manage\/staff\/drivers(?:\/[^/]+)?$/.test(path)) {
    return held.includes(PERMISSIONS.DISPATCH_MANAGE);
  }
  return false;
}

/** May enter the shared workspace shell; each destination is gated separately. */
export function holdsBackOfficePermission(
  permissions: readonly string[] | undefined | null,
): boolean {
  const held = permissions ?? [];
  return (
    holdsAdministrationPermission(held) ||
    [
      "manage/invoices",
      "manage/shift",
      "manage/deliveries/new",
      "manage/customers",
      "manage/staff/drivers",
    ].some((path) => canAccessManagementPath(held, path))
  );
}

export function canUseBackOffice(session: SellerSession | undefined): boolean {
  return holdsBackOfficePermission(session?.seller.permissions);
}
